import { errorMessage } from "../../utils/mutationResult.js";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { fetchOrQueue, cancelOutboxItemsForEntity } from "../../lib/offlineQueue"
import { newKeyIssueId, selectKeyIssueMatrixItems, withoutKeyIssues, observationsWithKeyIssues } from "../../utils/keyIssueMatrix"

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8005/api"
const findObservation = (state, id) => [
  ...(state?.observations?.observations || []),
  ...(state?.auditReports?.currentReportData?.observations || []),
  ...Object.values(state?.auditReports?.allReportsData || {}).flatMap((report) => report?.observations || []),
].find((observation) => [observation?._id, observation?._clientId].includes(id))

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const mutateKeyIssueMatrix = createAsyncThunk("keyIssueMatrix/mutate", async (
  { observationId, riskId, action, riskData = {}, observation: providedObservation }, { dispatch, getState, rejectWithValue },
) => {
  try {
    const state = getState()
    const observation = findObservation(state, observationId) || providedObservation
    const reportId = observation?.reportId || state?.auditReports?.currentReport?._id
    const existing = selectKeyIssueMatrixItems(state, reportId).find((item) => String(item._id || item.id) === String(riskId))
    if (action === "update" && !existing) throw new Error("This key issue no longer exists")
    const id = action === "create" || action === "add" ? newKeyIssueId() : riskId
    if (!observation || !reportId || !id) throw new Error("The key issue must be linked to an observation and report")
    const fields = { ...riskData }
    for (const key of ["_id", "id", "_sourceId", "_offlineQueued", "observationId", "siteId", "reportId", "userId"]) delete fields[key]
    const item = { ...existing, ...fields, _id: id, observationId, reportId, siteId: observation.siteId,
      templateId: observation.templateId, isActive: action !== "delete" }
    if (action === "delete") {
      await cancelOutboxItemsForEntity(dispatch, { entityType: "key-issue-matrix", entityId: id })
      if (existing?._offlineQueued) return { item, action: "delete" }
    }
    const method = action === "delete" ? "DELETE" : action === "update" ? "PUT" : "POST"
    const url = `${API_BASE_URL}/key-issue-matrix${method === "POST" ? "" : `/${id}`}`
    const body = JSON.stringify({ ...fields, _id: id, observationId })
    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }
    const outcome = await fetchOrQueue({ dispatch, requestInit: { url, method, headers, body },
      meta: { entityType: "key-issue-matrix", entityId: id, observationId, reportId,
        parentEntityType: "observation", parentEntityId: observationId,
        requestType: `key-issue-${method === "POST" ? "create" : action}`, description: `${action} key issue ${fields.title || id}` },
      forceQueue: String(observationId).startsWith("offline") || existing?._offlineQueued,
      performRequest: async () => {
        const response = await fetch(url, { method, headers, body, credentials: "include" })
        const payload = await response.json()
        if (!response.ok) throw Object.assign(new Error(errorMessage(payload, "Key issue save failed")), { status: response.status })
        return payload.data
      },
    })
    return { item: outcome.queued ? { ...item, _offlineQueued: method === "POST" || existing?._offlineQueued } : outcome.result,
      action, queued: outcome.queued }
  } catch (error) { return rejectWithValue(errorMessage(error, "The request failed. Please try again.")) }
})

// Return a joined editor view for import photo matching, while the mutation
// action updates the separate matrix list in report/offline storage.
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const saveLinkedKeyIssue = createAsyncThunk("keyIssueMatrix/saveLinked", async (args, { dispatch, getState }) => {
  const { item } = await dispatch(mutateKeyIssueMatrix(args)).unwrap()
  const observation = findObservation(getState(), args.observationId)
  return observationsWithKeyIssues([withoutKeyIssues(observation)], selectKeyIssueMatrixItems(getState(), item.reportId))[0]
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const createObservationWithKeyIssues = createAsyncThunk("keyIssueMatrix/createObservation", async (args, { dispatch }) => {
  const { createObservation } = await import("./observationSlice")
  return dispatch(createObservation(args)).unwrap()
})

const slice = createSlice({ name: "keyIssueMatrix", initialState: { items: [], loading: false, error: null }, reducers: {
  applySyncedKeyIssue: (state, { payload }) => {
    state.items = state.items.filter((item) => item._id !== payload.item._id);
    if (payload.item.isActive !== false) state.items.push(payload.item);
  },
},
  extraReducers: (builder) => builder
    .addCase(mutateKeyIssueMatrix.pending, (state) => { state.loading = true; state.error = null })
    .addCase(mutateKeyIssueMatrix.fulfilled, (state, { payload }) => {
      state.loading = false
      state.items = state.items.filter((item) => item._id !== payload.item._id)
      if (payload.action !== "delete") state.items.push(payload.item)
    })
    .addCase(mutateKeyIssueMatrix.rejected, (state, action) => { state.loading = false; state.error = action.payload }),
})
export default slice.reducer
export const { applySyncedKeyIssue } = slice.actions
